import { FC } from 'react';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Email, Delete, OpenInNew } from '@mui/icons-material';
import {
  useListOrganisationUsers,
  useOrganisationUser,
} from 'hooks/data/organisation/useOrganisationUsers';
import useOrganisationPlatformInvites from 'hooks/data/organisation/useOrganisationPlatformInvites';
import Table, { TableActions } from 'components/Table';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useNotification } from 'hooks/ui/useNotification';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import { NavLink } from 'react-router-dom';
import { createOrganisationAddUserRoute } from 'adminConstants/routes';
import { FirmSelector } from 'components/FirmSelector';
import { useLaunchPortalImpersonation } from 'hooks/data/impersonationAccess/useLaunchPortalImpersonation';
import api from 'lib/trpcClient';

type Props = {
  organisationId: string;
};

const useStyles = makeStyles()(() => ({
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  container: {
    marginBottom: '1.5rem',
  },
  title: {
    color: '#656565',
    margin: '1.5rem 0',
  },
}));

const OrganisationUsers: FC<Props> = ({ organisationId }) => {
  const { classes } = useStyles();
  const { isSuperAdmin, firmId } = useGetRole();
  const { success } = useNotification();
  const [createPermission, deletePermission, impersonatePermission] =
    useHasPermissions([
      RolePermission.CreateOrganisationUsers,
      RolePermission.DeleteOrganisationUsers,
      RolePermission.UseImpersonationAccessOrgUser,
    ]);
  const deleteDisclosure = useDisclosure();
  const impersonationDisclosure = useDisclosure();

  const { data, isFetching } = useListOrganisationUsers({ organisationId });
  const { resendInvite } = useOrganisationPlatformInvites({
    onResendInviteSuccess: () => {
      success('Organisation invitation sent successfully.');
    },
  });
  const { remove } = useOrganisationUser({ organisationId });
  const launchPortalImpersonation = useLaunchPortalImpersonation();
  const { data: eisFirm } = api.eisFirm.getById.useQuery(
    { firmId: firmId ?? '' },
    { enabled: !!firmId },
  );

  const handleImpersonation = ({ id }: { id: string }) => {
    if (isSuperAdmin) {
      impersonationDisclosure.stageAction({ id });
    } else {
      launchPortalImpersonation.orgUser({
        organisationUserId: id,
        firmDomain: eisFirm?.domain ?? '',
      });
    }
  };

  const columns = [
    { label: 'First name', key: 'firstName' },
    { label: 'Last name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    {
      label: 'Actions',
      key: 'actions',
      render: (row) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              icon: Delete,
              color: 'error',
              visible: deletePermission,
              label: 'Delete',
              onClick: () => deleteDisclosure.stageAction({ id: row._id }),
            },
            {
              icon: Email,
              color: 'primary',
              disabled: isSuperAdmin || resendInvite.isPending,
              label: 'Resend invitation',
              tooltip: isSuperAdmin
                ? 'Resending organisation invites is only possible for firm admin users.'
                : 'Resend invitation to this user.',
              onClick: (e) => {
                e?.preventDefault();
                resendInvite.mutate(row._id);
              },
            },
            {
              icon: OpenInNew,
              color: 'primary',
              visible: impersonatePermission,
              label: 'Launch portal',
              onClick: () => handleImpersonation({ id: row._id }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.flex}>
        <h2 className={classes.title}>Organisation users</h2>
        {createPermission && (
          <NavLink to={createOrganisationAddUserRoute(organisationId)}>
            <Button color="primary" variant="outlined">
              Add user
            </Button>
          </NavLink>
        )}
      </div>
      <Table
        loading={isFetching}
        tablebody={data ?? []}
        columns={columns}
        emptyMessage="No organisation users found"
      />
      <AlertDialog
        open={deleteDisclosure.isOpen}
        title="Delete organisation user"
        content="Are you sure you want to delete this user?"
        onClose={deleteDisclosure.onClose}
        onConfirm={() => {
          remove.mutate(deleteDisclosure.actionProps.id);
          deleteDisclosure.onClose();
        }}
        btnLabels={{ confirm: 'Delete user', cancel: 'Cancel' }}
      />
      {impersonatePermission && isSuperAdmin && (
        <AlertDialog
          open={impersonationDisclosure.isOpen}
          onClose={impersonationDisclosure.onClose}
          title="Choose which firms portal to view"
          content={
            <FirmSelector
              onChange={(_firmId, firm) => {
                if (!firm?.domain) {
                  return;
                }
                launchPortalImpersonation.orgUser({
                  organisationUserId: impersonationDisclosure.actionProps.id,
                  firmDomain: firm.domain,
                });
                impersonationDisclosure.onClose();
              }}
            />
          }
          btnLabels={{ cancel: 'Close' }}
        />
      )}
    </div>
  );
};

export default OrganisationUsers;
